import { Tabs, type TabsProps } from 'antd';
import { CanceledError } from 'axios';
import { Formik } from 'formik';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import './CustomerLeadStepAdditionalInformation.scss';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import type { ICustomerLeadProductPurchaseProductTypesTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import {
  customerLeadProductAdditionalInformationInitialValues,
  customerLeadProductAdditionalInformationValidationSchema,
} from 'src/components/customers/steps/AdditionalInformation/CustomerLeadStepAdditionalInformation.validation';
import { CustomerLeadStepPurchaseItemsTab } from 'src/components/customers/steps/AdditionalInformation/CustomerLeadStepPurchaseItemsTab';
import { CustomerLeadStepLayout } from 'src/components/customers/steps/CustomerLeadStepLayout';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

type CustomerLeadStepAdditionalInformationProps = {
  customerProductLeadId: string;
};

export function CustomerLeadStepAdditionalInformation({
  customerProductLeadId,
}: Readonly<CustomerLeadStepAdditionalInformationProps>) {
  const { isEditing, customerLeadProduct, updateCustomerLeadProduct, setCustomerStepGuideKey } =
    useContext(CustomersContext);

  const [, partialUpdateCustomerLead, cancelPartialUpdateCustomerLead] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadProductPurchaseProductTypesTabFormValues, submit: boolean) => {
      cancelPartialUpdateCustomerLead();

      try {
        const updatedCustomerProductLeadData = await partialUpdateCustomerLead({
          data: {
            additionalInfo: {
              productTypes: newValues.purchaseProductTypes,
              customerPickup: newValues.customerPickup,

              submit,
            },
          },
        });

        if (submit) {
          updateCustomerLeadProduct(updatedCustomerProductLeadData.data);
        }
      } catch (unknownError) {
        if (unknownError instanceof CanceledError) {
          console.debug('AdditionalInformation: auto save request has been cancelled');
        } else {
          console.error(unknownError);
        }
      }
    },
    [cancelPartialUpdateCustomerLead, partialUpdateCustomerLead, updateCustomerLeadProduct]
  );

  const handleValuesChange = useCallback(
    (saveValues: ICustomerLeadProductPurchaseProductTypesTabFormValues) => save(saveValues, false),
    [save]
  );

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(
    () => [
      {
        key: ProductSpecificationFieldGroup.PurchaseItems,
        label: 'Purchase Other Items',
        disabled: isEditing,
        children: <CustomerLeadStepPurchaseItemsTab onChange={handleValuesChange} />,
      },
    ],
    [isEditing, handleValuesChange]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadProductPurchaseProductTypesTabFormValues) => {
      await save(submitValues, true);

      return true;
    },
    [save]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setCustomerStepGuideKey(ProductSpecificationFieldGroup.PurchaseItems);
  }, []);

  if (!customerLeadProduct) {
    return null;
  }

  return (
    <Formik<ICustomerLeadProductPurchaseProductTypesTabFormValues>
      initialValues={customerLeadProductAdditionalInformationInitialValues(customerLeadProduct)}
      validationSchema={customerLeadProductAdditionalInformationValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      <CustomerLeadStepLayout>
        <div className="customer-lead-step-additional-information__container">
          <Tabs
            items={tabs}
            animated={false}
            destroyInactiveTabPane={false}
            activeKey={ProductSpecificationFieldGroup.PurchaseItems}
          />
        </div>
      </CustomerLeadStepLayout>
    </Formik>
  );
}
